/**
 * Entity creation, operations, delete/trash flows, linking, protocols and audit logs.
 *
 * One slice of the `ANALYTICS_EVENTS` SSOT — see `../analytics.ts`, which
 * spreads every slice back into the single import surface call sites use.
 * The event NAME STRINGS are a PostHog dashboard contract: add and retire keys
 * here, never rename a value in place (see `rules/analytics.md`).
 */

export const ENTITY_EVENTS = {
  // Entity Creation
  COLLECTION_CREATED: 'Collection Created',
  ATTACHMENT_UPLOADED: 'Artifact Uploaded',
  CONTENT_CREATED: 'Content Created',
  // Native spreadsheets. Counts and booleans only, never cell values.
  SPREADSHEET_IMPORTED: 'Spreadsheet Imported',
  SPREADSHEET_CREATED: 'Spreadsheet Created',
  SPREADSHEET_PRINTED: 'Spreadsheet Printed',
  SPREADSHEET_OPENED: 'Spreadsheet Opened',
  SPREADSHEET_EXPORTED: 'Spreadsheet Exported',
  /**
   * Univer's version history opened on a spreadsheet. `source: 'change_card'`
   * when the chat change card asked for it (after a refused or unconfirmed
   * undo); no properties when opened from the page's own menu.
   */
  SPREADSHEET_HISTORY_OPENED: 'Spreadsheet History Opened',
  SPREADSHEET_FEATURE_SEARCH_OPENED: 'Spreadsheet Feature Search Opened',
  // A toggle: Univer's command opens or closes the panel.
  SPREADSHEET_SHORTCUTS_TOGGLED: 'Spreadsheet Shortcuts Toggled',
  SPREADSHEET_FIND_USED: 'Spreadsheet Find Used',
  // Retry import on a spreadsheet whose background import failed.
  SPREADSHEET_IMPORT_RETRIED: 'Spreadsheet Import Retried',
  /** "Convert to spreadsheet" on an uploaded CSV/XLSX. Properties: format. */
  SPREADSHEET_CONVERT_CLICKED: 'Spreadsheet Convert Clicked',
  /**
   * An edit waited 30 s with no progress and the grid was locked, or a grid
   * was torn down with an edit waiting (`stuckSave.ts`). Properties:
   * last_status (Univer's status word), reason ('stuck' | 'teardown'),
   * kept_on_device (the edits were written to this device).
   */
  SPREADSHEET_SAVE_STOPPED: 'Spreadsheet Save Stopped',
  /**
   * Edits a stopped save would have lost are safe. Properties: outcome
   * ('copy' a new spreadsheet, 'download' an .xlsx, 'download_raw' the
   * workbook's data when no .xlsx could be made), source ('stuck' this tab,
   * 'device' found on the device).
   */
  SPREADSHEET_UNSAVED_CHANGES_KEPT: 'Spreadsheet Unsaved Changes Kept',
  /** The person discarded edits kept on the device. No properties. */
  SPREADSHEET_UNSAVED_CHANGES_DISCARDED: 'Spreadsheet Unsaved Changes Discarded',
  // Agent-service tools/spreadsheet_unsupported.py fires this when the
  // spreadsheet specialist is asked for a change outside its v1 op list.
  // Properties: category (a closed list of OUR words), never the request.
  // Listed here for grep affinity, NOT because the frontend emits it.
  SPREADSHEET_AGENT_UNSUPPORTED_REQUEST: 'Spreadsheet Agent Unsupported Request',
  // Link artifacts (docs/planning/link-artifacts.md). NEVER the URL, its
  // hostname or the title in properties: they are user content.
  /** Properties: surface, title_source ('page'|'hostname'|'user'), icon ('site'|'bower'). */
  LINK_CREATED: 'Link Created',
  /** Properties: surface. */
  LINK_OPENED: 'Link Opened',
  /** Properties: changed (array of 'url'|'title'|'icon'). */
  LINK_EDITED: 'Link Edited',

  // Entity Operations
  NOTE_DELETED: 'Artifact Deleted',
  COLLECTION_UPDATED: 'Collection Updated',
  ATTACHMENT_DELETED: 'Artifact Deleted',
  BULK_OPERATION_COMPLETED: 'Bulk Operation Completed',
  NOTES_MERGED: 'Notes Merged',
  /**
   * Ctrl/Cmd+S pressed on a writable note. Properties: had_unsaved_changes
   * (true → the autosave queue was flushed; false → the "already saved"
   * toast). The false share is the size of the habit the shortcut absorbs.
   */
  NOTE_SAVE_SHORTCUT: 'Note Save Shortcut',
  /**
   * The top bar's Retry save button pressed after a failed save (the touch
   * way to force a save once autosave ran out of retries), tracked once the
   * retry settles. Properties: succeeded (boolean).
   */
  NOTE_SAVE_RETRIED: 'Note Save Retried',

  // Delete Flows
  DELETE_CONFIRMED: 'Delete Confirmed',
  DELETE_CANCELLED: 'Delete Cancelled',

  // Trash
  ITEM_TRASHED: 'Item Trashed',
  ITEM_RESTORED: 'Item Restored',
  ITEM_PERMANENTLY_DELETED: 'Item Permanently Deleted',
  TRASH_VIEWED: 'Trash Viewed',
  TRASH_UNDO_USED: 'Trash Undo Used',

  // Entity Linking
  ENTITY_LINKED: 'Entity Linked',
  ENTITY_UNLINKED: 'Entity Unlinked',
  INFERRED_LINK_PROMOTED: 'Inferred Link Promoted',
  INFERRED_LINK_DISMISSED: 'Inferred Link Dismissed',

  // Protocols
  PROTOCOL_CREATED: 'Protocol Created',

  // Audit Logs
  AUDIT_LOG_VIEWED: 'Audit Log Viewed',
} as const;
